import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/Store/index.ts";
import { Button, Container } from "react-bootstrap";
import SideBar from "../Components/SideBar.tsx";
import Header from "../Components/Header.tsx";
import "./Home.css";
import Tabelle from "../Components/Table.tsx";
import { CantieriColumns } from "../lib/ColonneTabelle.ts";
import FiltroSidenav from "../Components/FiltroSidenav.tsx";
import { useNavigate } from "react-router-dom";
import { Cantieri } from "../lib/interface.ts";

const CantieriPage: React.FC = () => {
  const cantieri = useSelector((state: RootState) => state.cantieri.cantieri);
  const contratti = useSelector((state: RootState) => state.contratti.contratti);
  const stati = useSelector((state: RootState) => state.stati.stati);
  const [cantieriTable, setCantieriTable] = useState<Cantieri[]>([]);
  const [cantieriFiltrati, setCantieriFiltrati] = useState<Cantieri[]>([]);
  const [archived, setArchived] = useState(false);
  const today = new Date();

  const formatDateIT = (isoDateString?: string): string => {
    if (!isoDateString) return "";
    const parts = isoDateString.split("-");
    if (parts.length !== 3) return isoDateString;
    const [yyyy, mm, dd] = parts;
    return `${dd}/${mm}/${yyyy}`;
  };
  const getColorBadge = (stato: string) => {
    const singoloStato = stati.find((s) => s.id == stato);
    if (singoloStato) return "#" + singoloStato.colore;
    else return "grey";
  };
  const getLabelStato = (stato: string) => {
    const singoloStato = stati.find((s) => s.id == stato);
    if (singoloStato) return singoloStato.stato;
    else return "nessuno stato";
  };

  const filterCantieri = (cantieri: Cantieri[], archived: boolean) => {
    return cantieri.filter((cantiere) => {
      const dataFineEffettiva = cantiere.dataFineEffettiva ? new Date(cantiere.dataFineEffettiva) : today;
      return archived ? dataFineEffettiva < today : dataFineEffettiva >= today;
    });
  };

  const prepareCantieriForTable = (cantieri: Cantieri[]): Cantieri[] => {
    return cantieri.map((cantiere) => ({
      ...cantiere,
      codiceContratto: contratti.find((contratto) => contratto.id === cantiere.contrattoQuadro)?.codice || "N/A",
      nomeReferente: contratti.find((contratto) => contratto.id === cantiere.contrattoQuadro)?.referenti?.[0]
        ? `${contratti.find((contratto) => contratto.id === cantiere.contrattoQuadro)?.referenti?.[0]?.nome} ${
            contratti.find((contratto) => contratto.id === cantiere.contrattoQuadro)?.referenti?.[0]?.cognome
          }`
        : "Nessun referente",
      subappalto: cantiere.subappalto === "true" ? "Sì" : "No",
      dataInizio: formatDateIT(cantiere.dataInizio),
      dataFinePrevista: formatDateIT(cantiere.dataFinePrevista),
      importo: new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
      }).format(cantiere.importo || 0),
    }));
  };

  useEffect(() => {
    if (cantieri.length > 0 && contratti.length > 0) {
      // Prepara e filtra i cantieri per la visualizzazione
      const cantieriPreparati = prepareCantieriForTable(cantieri);

      const cantieriFiltrati = filterCantieri(cantieriPreparati, archived);

      setCantieriTable(cantieriFiltrati);
    }
  }, [cantieri, contratti, archived]);

  useEffect(() => {
    // Imposta i cantieri "in essere" al primo montaggio dei filtri
    if (cantieriFiltrati.length > 0) setCantieriTable(filterCantieri(prepareCantieriForTable(cantieriFiltrati), false));
  }, [cantieriFiltrati]);

  const [showFiltro, setShowFiltro] = useState(false);
  const navigate = useNavigate();

  const handleArchivedClick = () => {
    const nuovoStato = !archived;
    setArchived(nuovoStato);
    setCantieriTable(filterCantieri(prepareCantieriForTable(cantieri), nuovoStato));
  };

  const handleFilterClick = () => setShowFiltro(true);
  const handleAddClick = () => navigate("/crea/cantieri");

  return (
    <Container fluid className="d-flex justify-content-between" style={{ margin: "0", padding: "0" }}>
      <Container fluid style={{ margin: "0", padding: "0", width: "5%" }}>
        <SideBar />
      </Container>
      <Container fluid className="colonna">
        {contratti.length > 0 && (
          <div className="d-flex justify-content-center">
            <Header
              title={archived ? "Cantieri Archiviati" : "Cantieri in Essere"}
              buttons={[
                {
                  label: archived ? "In essere" : "Archiviati", // Cambia testo dinamicamente
                  onClick: handleArchivedClick,
                },
                { label: "Filtra", onClick: handleFilterClick },
                {
                  label: "Aggiungi un nuovo cantiere",
                  onClick: handleAddClick,
                },
              ]}
            />
          </div>
        )}
        <div className="d-flex justify-content-center">
          {contratti.length > 0 ? (
            <div className="table-card">
              <Tabelle
                data={cantieriTable.map((cantiere) => ({
                  ...cantiere,
                  stato: (
                    <div
                      className="badge"
                      style={{ backgroundColor: getColorBadge(cantiere.stato) }}
                      aria-label={`Stato: ${cantiere.stato}`}
                    >
                      {getLabelStato(cantiere.stato) || "-"}
                    </div>
                  ),
                }))}
                columns={CantieriColumns}
                type={"cantieri"}
                tableTitle="Cantieri"
              />
            </div>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "80vh" }}>
              <h2 className="text-center">Per creare un Cantiere devi prima aver creato un Contratto</h2>
              <Button className="button mt-3" onClick={() => navigate("/contratti")}>
                vai a contratti
              </Button>
            </div>
          )}
        </div>
      </Container>

      <FiltroSidenav
        visible={showFiltro}
        onHide={() => setShowFiltro(false)}
        setTable={(c) => setCantieriFiltrati(c)}
      />
    </Container>
  );
};

export default CantieriPage;
